export const logarithmLessons = [
  {
    id: 'precalc-4',
    title: 'Logarithmic Functions',
    unit: 'Advanced Functions',
    description: 'Understand logarithms as inverses of exponentials, properties, and solving logarithmic equations',
    standards: ['CCSS.MATH.CONTENT.HSF.BF.B.5', 'NCTM: Algebra', 'Ohio: PC.F.4', 'NY: AI-F.BF.5'],
    vocabulary: [
      { term: 'Logarithm', definition: 'The inverse of an exponential function: if bˣ = y, then log_b(y) = x' },
      { term: 'Common Logarithm', definition: 'Logarithm with base 10, written as log(x)' },
      { term: 'Natural Logarithm', definition: 'Logarithm with base e, written as ln(x)' },
      { term: 'Change of Base Formula', definition: 'log_b(x) = log(x)/log(b) = ln(x)/ln(b)' },
      { term: 'Product Property', definition: 'log_b(xy) = log_b(x) + log_b(y)' },
      { term: 'Quotient Property', definition: 'log_b(x/y) = log_b(x) - log_b(y)' },
      { term: 'Power Property', definition: 'log_b(xⁿ) = n·log_b(x)' },
      { term: 'Domain', definition: 'For f(x) = log_b(x), domain is x &gt; 0' }
    ],
    concepts: [
      'Logarithms are the inverse of exponential functions',
      'log_b(x) asks: "To what power must b be raised to get x?"',
      'The domain of log_b(x) is x &gt; 0; range is all real numbers',
      'Vertical asymptote at x = 0 (y-axis)',
      'log_b(b) = 1 and log_b(1) = 0 for any base b',
      'Properties allow simplification and solving complex equations'
    ],
    examples: [
      {
        problem: 'Solve: log₂(x) = 5',
        steps: [
          'Rewrite in exponential form: 2⁵ = x',
          'Calculate: x = 32',
          'Check: log₂(32) = log₂(2⁵) = 5 ✓'
        ]
      },
      {
        problem: 'Expand: log₃(x²y/z)',
        steps: [
          'Apply quotient property: log₃(x²y) - log₃(z)',
          'Apply product property: log₃(x²) + log₃(y) - log₃(z)',
          'Apply power property: 2log₃(x) + log₃(y) - log₃(z)'
        ]
      }
    ],
    teacherTip: 'Emphasize that logarithms answer the question "what exponent?" This helps students understand the inverse relationship with exponentials.',
    questions: [
      {
        id: 'log-1',
        question: 'What is log₂(8)?',
        options: ['2', '3', '4', '8'],
        correctAnswer: 1,
        explanation: 'log₂(8) asks: 2 to what power equals 8? 2³ = 8, so log₂(8) = 3.'
      },
      {
        id: 'log-2',
        question: 'What is log₁₀(1000)?',
        options: ['2', '3', '10', '100'],
        correctAnswer: 1,
        explanation: '10³ = 1000, so log₁₀(1000) = 3.'
      },
      {
        id: 'log-3',
        question: 'What is log_b(1) for any base b?',
        options: ['-1', '0', '1', 'b'],
        correctAnswer: 1,
        explanation: 'b⁰ = 1 for any base, so log_b(1) = 0.'
      },
      {
        id: 'log-4',
        question: 'What is log_b(b)?',
        options: ['0', '1', 'b', 'b²'],
        correctAnswer: 1,
        explanation: 'b¹ = b, so log_b(b) = 1.'
      },
      {
        id: 'log-5',
        question: 'What is the domain of f(x) = log(x)?',
        options: ['All real numbers', 'x &gt; 0', 'x ≥ 0', 'x ≠ 0'],
        correctAnswer: 1,
        explanation: 'Logarithms are only defined for positive numbers: x &gt; 0.'
      },
      {
        id: 'log-6',
        question: 'What is the vertical asymptote of f(x) = log₂(x)?',
        options: ['x = 0', 'x = 1', 'x = 2', 'y = 0'],
        correctAnswer: 0,
        explanation: 'All logarithmic functions have vertical asymptote at x = 0 (y-axis).'
      },
      {
        id: 'log-7',
        question: 'Simplify: log₅(25)',
        options: ['2', '5', '10', '25'],
        correctAnswer: 0,
        explanation: '5² = 25, so log₅(25) = 2.'
      },
      {
        id: 'log-8',
        question: 'What is log₃(1/9)?',
        options: ['-2', '-1', '1', '2'],
        correctAnswer: 0,
        explanation: '3⁻² = 1/9, so log₃(1/9) = -2.'
      },
      {
        id: 'log-9',
        question: 'Using the product property, log(xy) equals:',
        options: ['log(x) · log(y)', 'log(x) + log(y)', 'log(x) - log(y)', 'log(x)/log(y)'],
        correctAnswer: 1,
        explanation: 'Product property: log(xy) = log(x) + log(y).'
      },
      {
        id: 'log-10',
        question: 'Using the quotient property, log(x/y) equals:',
        options: ['log(x)/log(y)', 'log(x) - log(y)', 'log(x) + log(y)', 'log(x) · log(y)'],
        correctAnswer: 1,
        explanation: 'Quotient property: log(x/y) = log(x) - log(y).'
      },
      {
        id: 'log-11',
        question: 'Using the power property, log(x³) equals:',
        options: ['3 + log(x)', '3 · log(x)', 'log(x)/3', '[log(x)]³'],
        correctAnswer: 1,
        explanation: 'Power property: log(xⁿ) = n·log(x), so log(x³) = 3·log(x).'
      },
      {
        id: 'log-12',
        question: 'Solve: log₄(x) = 3',
        options: ['12', '16', '64', '81'],
        correctAnswer: 2,
        explanation: 'Rewrite: 4³ = x, so x = 64.'
      },
      {
        id: 'log-13',
        question: 'What is ln(e)?',
        options: ['0', '1', 'e', 'undefined'],
        correctAnswer: 1,
        explanation: 'ln is log base e, so ln(e) = log_e(e) = 1.'
      },
      {
        id: 'log-14',
        question: 'What is ln(1)?',
        options: ['-1', '0', '1', 'e'],
        correctAnswer: 1,
        explanation: 'e⁰ = 1, so ln(1) = 0.'
      },
      {
        id: 'log-15',
        question: 'Expand: log₂(8x)',
        options: ['log₂(8) + log₂(x)', '8·log₂(x)', 'log₂(8) · log₂(x)', '3 + x'],
        correctAnswer: 0,
        explanation: 'Product property: log₂(8x) = log₂(8) + log₂(x) = 3 + log₂(x).'
      },
      {
        id: 'log-16',
        question: 'Condense: 2log(x) + log(y)',
        options: ['log(2xy)', 'log(x²y)', 'log(x² + y)', '2log(xy)'],
        correctAnswer: 1,
        explanation: '2log(x) = log(x²), then log(x²) + log(y) = log(x²y).'
      },
      {
        id: 'log-17',
        question: 'What is the range of f(x) = log₃(x)?',
        options: ['x &gt; 0', 'y &gt; 0', 'All real numbers', 'y ≥ 0'],
        correctAnswer: 2,
        explanation: 'Logarithmic functions have range of all real numbers.'
      },
      {
        id: 'log-18',
        question: 'Solve: log(x) + log(x + 3) = 1',
        options: ['x = 2', 'x = 3', 'x = 5', 'x = 7'],
        correctAnswer: 0,
        explanation: 'log[x(x+3)] = 1, so x(x+3) = 10, x² + 3x - 10 = 0, (x+5)(x-2) = 0. x = 2 (x = -5 rejected).'
      },
      {
        id: 'log-19',
        question: 'What is the change of base formula for log₅(x)?',
        options: ['log(x)/log(5)', 'log(5)/log(x)', '5·log(x)', 'log(x) - log(5)'],
        correctAnswer: 0,
        explanation: 'Change of base: log₅(x) = log(x)/log(5) = ln(x)/ln(5).'
      },
      {
        id: 'log-20',
        question: 'If log₂(x) = 4, what is x?',
        options: ['2', '4', '8', '16'],
        correctAnswer: 3,
        explanation: '2⁴ = x, so x = 16.'
      },
      {
        id: 'log-21',
        question: 'Simplify: log₇(7⁵)',
        options: ['5', '7', '35', '7⁵'],
        correctAnswer: 0,
        explanation: 'log_b(bˣ) = x, so log₇(7⁵) = 5.'
      },
      {
        id: 'log-22',
        question: 'What is 10^(log(x))?',
        options: ['10', 'x', 'log(x)', '10x'],
        correctAnswer: 1,
        explanation: 'Exponential and logarithm are inverses: b^(log_b(x)) = x.'
      },
      {
        id: 'log-23',
        question: 'Solve: 2^x = 32',
        options: ['x = 4', 'x = 5', 'x = 6', 'x = 16'],
        correctAnswer: 1,
        explanation: 'Take log₂ of both sides: x = log₂(32) = log₂(2⁵) = 5.'
      },
      {
        id: 'log-24',
        question: 'What is log₄(2)?',
        options: ['1/4', '1/2', '2', '4'],
        correctAnswer: 1,
        explanation: '4^(1/2) = √4 = 2, so log₄(2) = 1/2.'
      },
      {
        id: 'log-25',
        question: 'Expand: ln(e²x³)',
        options: ['2 + 3ln(x)', '2ln(e) + 3ln(x)', '5ln(ex)', '6ln(ex)'],
        correctAnswer: 0,
        explanation: 'ln(e²x³) = ln(e²) + ln(x³) = 2ln(e) + 3ln(x) = 2(1) + 3ln(x) = 2 + 3ln(x).'
      },
      {
        id: 'log-26',
        question: 'If ln(x) = 3, what is x?',
        options: ['3', 'e³', '3e', 'e/3'],
        correctAnswer: 1,
        explanation: 'ln(x) = 3 means e³ = x, so x = e³ ≈ 20.09.'
      },
      {
        id: 'log-27',
        question: 'Solve: log₃(x - 2) = 2',
        options: ['x = 7', 'x = 9', 'x = 11', 'x = 13'],
        correctAnswer: 2,
        explanation: '3² = x - 2, so 9 = x - 2, x = 11.'
      },
      {
        id: 'log-28',
        question: 'What is log(0.01)?',
        options: ['-3', '-2', '-1', '0'],
        correctAnswer: 1,
        explanation: '0.01 = 1/100 = 10⁻², so log(0.01) = -2.'
      },
      {
        id: 'log-29',
        question: 'Condense: 3log(x) - log(y)',
        options: ['log(3x - y)', 'log(x³/y)', 'log(x³) - log(y)', '3log(x/y)'],
        correctAnswer: 1,
        explanation: '3log(x) = log(x³), then log(x³) - log(y) = log(x³/y).'
      },
      {
        id: 'log-30',
        question: 'What is the inverse of f(x) = log₂(x)?',
        options: ['f⁻¹(x) = 2ˣ', 'f⁻¹(x) = x²', 'f⁻¹(x) = 2/x', 'f⁻¹(x) = √x'],
        correctAnswer: 0,
        explanation: 'The inverse of a logarithm is an exponential: f⁻¹(x) = 2ˣ.'
      }
    ]
  },
  {
    id: 'precalc-5',
    title: 'Function Composition & Inverses',
    unit: 'Advanced Functions',
    description: 'Master function composition, finding inverses, and determining invertibility',
    standards: ['CCSS.MATH.CONTENT.HSF.BF.B.4', 'NCTM: Algebra', 'Ohio: PC.F.5', 'NY: AI-F.BF.4'],
    vocabulary: [
      { term: 'Composition', definition: '(f ∘ g)(x) = f(g(x)) - applying one function to the result of another' },
      { term: 'Inverse Function', definition: 'f⁻¹(x) undoes f(x): f(f⁻¹(x)) = x and f⁻¹(f(x)) = x' },
      { term: 'One-to-One', definition: 'A function where each output corresponds to exactly one input' },
      { term: 'Horizontal Line Test', definition: 'A function is one-to-one if no horizontal line intersects the graph more than once' },
      { term: 'Domain Restriction', definition: 'Limiting the domain to make a function one-to-one' },
      { term: 'Reflection Property', definition: 'The graph of f⁻¹ is the reflection of f across y = x' },
      { term: 'Identity Function', definition: 'f(x) = x, the result of composing a function with its inverse' },
      { term: 'Decomposition', definition: 'Breaking a complex function into simpler component functions' }
    ],
    concepts: [
      'Composition (f ∘ g)(x) means "f of g of x" - work from inside out',
      'Order matters: (f ∘ g)(x) ≠ (g ∘ f)(x) in general',
      'A function has an inverse only if it is one-to-one (passes horizontal line test)',
      'To find f⁻¹: swap x and y, then solve for y',
      'Domain of f = Range of f⁻¹, and Range of f = Domain of f⁻¹',
      'Verify inverse: check that f(f⁻¹(x)) = x and f⁻¹(f(x)) = x'
    ],
    examples: [
      {
        problem: 'If f(x) = 2x + 3 and g(x) = x² - 1, find (f ∘ g)(x)',
        steps: [
          '(f ∘ g)(x) = f(g(x))',
          'Substitute g(x) into f: f(x² - 1)',
          'Apply f: 2(x² - 1) + 3',
          'Simplify: 2x² - 2 + 3 = 2x² + 1'
        ]
      },
      {
        problem: 'Find the inverse of f(x) = (3x - 5)/2',
        steps: [
          'Replace f(x) with y: y = (3x - 5)/2',
          'Swap x and y: x = (3y - 5)/2',
          'Solve for y: 2x = 3y - 5',
          '2x + 5 = 3y',
          'y = (2x + 5)/3',
          'Therefore: f⁻¹(x) = (2x + 5)/3'
        ]
      }
    ],
    teacherTip: 'Use real-world examples like converting units (Celsius to Fahrenheit and back) to illustrate inverse functions. Emphasize that composition is like a "function machine" where output of one becomes input of another.',
    questions: [
      {
        id: 'comp-1',
        question: 'If f(x) = x + 2 and g(x) = 3x, what is (f ∘ g)(x)?',
        options: ['3x + 2', '3x + 6', 'x + 6', '3(x + 2)'],
        correctAnswer: 0,
        explanation: '(f ∘ g)(x) = f(g(x)) = f(3x) = 3x + 2.'
      },
      {
        id: 'comp-2',
        question: 'If f(x) = x² and g(x) = x + 1, what is (g ∘ f)(x)?',
        options: ['x² + 1', '(x + 1)²', 'x² + x + 1', 'x³ + x²'],
        correctAnswer: 0,
        explanation: '(g ∘ f)(x) = g(f(x)) = g(x²) = x² + 1.'
      },
      {
        id: 'comp-3',
        question: 'What is the inverse of f(x) = x + 5?',
        options: ['f⁻¹(x) = x - 5', 'f⁻¹(x) = 5 - x', 'f⁻¹(x) = x/5', 'f⁻¹(x) = 5x'],
        correctAnswer: 0,
        explanation: 'Swap and solve: x = y + 5, so y = x - 5. f⁻¹(x) = x - 5.'
      },
      {
        id: 'comp-4',
        question: 'What is the inverse of f(x) = 3x?',
        options: ['f⁻¹(x) = x/3', 'f⁻¹(x) = 3/x', 'f⁻¹(x) = -3x', 'f⁻¹(x) = x - 3'],
        correctAnswer: 0,
        explanation: 'x = 3y, so y = x/3. f⁻¹(x) = x/3.'
      },
      {
        id: 'comp-5',
        question: 'Which function is its own inverse?',
        options: ['f(x) = x²', 'f(x) = 2x', 'f(x) = -x', 'f(x) = x + 1'],
        correctAnswer: 2,
        explanation: 'f(x) = -x is its own inverse: f(f(x)) = f(-x) = -(-x) = x.'
      },
      {
        id: 'comp-6',
        question: 'Does f(x) = x² have an inverse?',
        options: ['Yes, always', 'No, never', 'Yes, with domain restriction', 'Only for x &gt; 0'],
        correctAnswer: 2,
        explanation: 'f(x) = x² fails horizontal line test, but restricting domain (e.g., x ≥ 0) makes it one-to-one.'
      },
      {
        id: 'comp-7',
        question: 'If f(x) = 2x - 1, what is f⁻¹(x)?',
        options: ['(x + 1)/2', '(x - 1)/2', '2x + 1', '1/(2x - 1)'],
        correctAnswer: 0,
        explanation: 'x = 2y - 1, so 2y = x + 1, y = (x + 1)/2.'
      },
      {
        id: 'comp-8',
        question: 'If f(x) = x³, what is f⁻¹(x)?',
        options: ['x^(1/3)', '3√x', 'Both A and B', '1/x³'],
        correctAnswer: 2,
        explanation: 'The inverse of cubing is taking the cube root: f⁻¹(x) = ∛x = x^(1/3).'
      },
      {
        id: 'comp-9',
        question: 'What is (f ∘ f⁻¹)(x)?',
        options: ['0', '1', 'x', 'f(x)'],
        correctAnswer: 2,
        explanation: 'Composing a function with its inverse gives the identity: (f ∘ f⁻¹)(x) = x.'
      },
      {
        id: 'comp-10',
        question: 'If f(x) = √x and g(x) = x², what is (f ∘ g)(x) for x ≥ 0?',
        options: ['x', 'x²', '√x', 'x⁴'],
        correctAnswer: 0,
        explanation: '(f ∘ g)(x) = f(x²) = √(x²) = |x| = x (for x ≥ 0).'
      },
      {
        id: 'comp-11',
        question: 'Which function passes the horizontal line test?',
        options: ['f(x) = x²', 'f(x) = |x|', 'f(x) = x³', 'f(x) = sin(x)'],
        correctAnswer: 2,
        explanation: 'f(x) = x³ is one-to-one and passes the horizontal line test.'
      },
      {
        id: 'comp-12',
        question: 'If f(x) = 1/x, what is f⁻¹(x)?',
        options: ['x', '-x', '1/x', 'x²'],
        correctAnswer: 2,
        explanation: 'f(x) = 1/x is its own inverse: f(f(x)) = f(1/x) = 1/(1/x) = x.'
      },
      {
        id: 'comp-13',
        question: 'If f(2) = 5 and g(5) = 8, what is (g ∘ f)(2)?',
        options: ['2', '5', '8', '13'],
        correctAnswer: 2,
        explanation: '(g ∘ f)(2) = g(f(2)) = g(5) = 8.'
      },
      {
        id: 'comp-14',
        question: 'What is the domain of f⁻¹ if the range of f is [0, 10]?',
        options: ['All real numbers', '[0, 10]', '(-∞, 0]', '[10, ∞)'],
        correctAnswer: 1,
        explanation: 'Domain of f⁻¹ = Range of f, so domain of f⁻¹ is [0, 10].'
      },
      {
        id: 'comp-15',
        question: 'If f(x) = (x - 3)/2, what is f⁻¹(x)?',
        options: ['2x + 3', '(x + 3)/2', '2x - 3', '2(x + 3)'],
        correctAnswer: 0,
        explanation: 'x = (y - 3)/2, so 2x = y - 3, y = 2x + 3.'
      },
      {
        id: 'comp-16',
        question: 'If f(x) = x + 1 and g(x) = x - 1, what is (f ∘ g)(x)?',
        options: ['x', 'x + 2', 'x - 2', '2x'],
        correctAnswer: 0,
        explanation: '(f ∘ g)(x) = f(x - 1) = (x - 1) + 1 = x.'
      },
      {
        id: 'comp-17',
        question: 'Are f(x) = x + 1 and g(x) = x - 1 inverses?',
        options: ['Yes', 'No', 'Only for x &gt; 0', 'Only for x &lt; 0'],
        correctAnswer: 0,
        explanation: 'f(g(x)) = (x - 1) + 1 = x and g(f(x)) = (x + 1) - 1 = x. They are inverses.'
      },
      {
        id: 'comp-18',
        question: 'What is the inverse of f(x) = x³ + 2?',
        options: ['f⁻¹(x) = ∛(x - 2)', 'f⁻¹(x) = ∛x - 2', 'f⁻¹(x) = (x - 2)³', 'f⁻¹(x) = x^(1/3) + 2'],
        correctAnswer: 0,
        explanation: 'x = y³ + 2, so y³ = x - 2, y = ∛(x - 2).'
      },
      {
        id: 'comp-19',
        question: 'If f(x) = 2x and g(x) = x/2, what is (f ∘ g)(x)?',
        options: ['x', '2x', 'x/2', '4x'],
        correctAnswer: 0,
        explanation: '(f ∘ g)(x) = f(x/2) = 2(x/2) = x.'
      },
      {
        id: 'comp-20',
        question: 'What is the graph of f⁻¹ relative to f?',
        options: ['Reflection across x-axis', 'Reflection across y-axis', 'Reflection across y = x', 'Rotation 90°'],
        correctAnswer: 2,
        explanation: 'The graph of f⁻¹ is the reflection of f across the line y = x.'
      },
      {
        id: 'comp-21',
        question: 'If f(x) = √(x - 1), what is the domain of f⁻¹?',
        options: ['x ≥ 0', 'x ≥ 1', 'x &gt; 0', 'All real numbers'],
        correctAnswer: 0,
        explanation: 'Range of f is y ≥ 0 (since √ gives non-negative), so domain of f⁻¹ is x ≥ 0.'
      },
      {
        id: 'comp-22',
        question: 'If f(x) = x² (x ≥ 0), what is f⁻¹(x)?',
        options: ['√x', '-√x', '±√x', 'x²'],
        correctAnswer: 0,
        explanation: 'With domain restriction x ≥ 0, f⁻¹(x) = √x.'
      },
      {
        id: 'comp-23',
        question: 'If f(x) = 5x - 3 and f⁻¹(x) = (x + b)/5, what is b?',
        options: ['3', '-3', '5', '-5'],
        correctAnswer: 0,
        explanation: 'Finding inverse: x = 5y - 3, so 5y = x + 3, y = (x + 3)/5. Thus b = 3.'
      },
      {
        id: 'comp-24',
        question: 'What is (f ∘ g)(x) if f(x) = x² and g(x) = √x?',
        options: ['x', '√(x²)', 'x²', '(√x)²'],
        correctAnswer: 0,
        explanation: '(f ∘ g)(x) = f(√x) = (√x)² = x.'
      },
      {
        id: 'comp-25',
        question: 'If f and g are inverses, what is (f ∘ g)(5)?',
        options: ['0', '1', '5', 'f(5)'],
        correctAnswer: 2,
        explanation: 'If f and g are inverses, (f ∘ g)(x) = x for all x, so (f ∘ g)(5) = 5.'
      },
      {
        id: 'comp-26',
        question: 'Which is NOT one-to-one?',
        options: ['f(x) = x³', 'f(x) = 2x + 1', 'f(x) = |x|', 'f(x) = 1/x'],
        correctAnswer: 2,
        explanation: 'f(x) = |x| fails horizontal line test: |-2| = |2| = 2.'
      },
      {
        id: 'comp-27',
        question: 'If f(x) = eˣ, what is f⁻¹(x)?',
        options: ['ln(x)', 'log(x)', '1/eˣ', 'e^(-x)'],
        correctAnswer: 0,
        explanation: 'The inverse of eˣ is ln(x).'
      },
      {
        id: 'comp-28',
        question: 'If f(x) = 3x + 2 and g(x) = (x - 2)/3, are they inverses?',
        options: ['Yes', 'No', 'Only for x &gt; 0', 'Cannot determine'],
        correctAnswer: 0,
        explanation: 'f(g(x)) = 3((x-2)/3) + 2 = x - 2 + 2 = x. g(f(x)) = (3x+2-2)/3 = x. Yes, inverses.'
      },
      {
        id: 'comp-29',
        question: 'What is the inverse of f(x) = -x + 4?',
        options: ['f⁻¹(x) = -x + 4', 'f⁻¹(x) = x - 4', 'f⁻¹(x) = 4 - x', 'f⁻¹(x) = -x - 4'],
        correctAnswer: 0,
        explanation: 'x = -y + 4, so -y = x - 4, y = -x + 4. This function is its own inverse.'
      },
      {
        id: 'comp-30',
        question: 'If f(x) = x⁵, what is f⁻¹(32)?',
        options: ['2', '5', '32', '1/32'],
        correctAnswer: 0,
        explanation: 'f⁻¹(x) = x^(1/5), so f⁻¹(32) = 32^(1/5) = ⁵√32 = 2.'
      }
    ]
  }
];